import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Skeleton, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';

import Fade from '@mui/material/Fade';
import css from './CreativeCard.css';
import { getCreativeSizeRatio } from '../../creativeSizingUtils';
import { CreativeMultiValuedDimensionsType, CreativeType, CreativeTypes } from '../../types';
import CreativeCardData from '../creativeCardData/CreativeCardData';
import CreativeAsset from '../creativeAsset/CreativeAsset';
import Tag from '../../../components/atoms/Tag';
import {
    ASYNC_FILTER_TO_FIELD_CONFIG,
    CAMPAIGN_NAME_FILTER,
    COUNTRY_FILTER,
    DimensionCategoryToColor,
    GeneralDimensionsConfig,
} from '../../dimensionsUtils';
import OverflowingText from '../../../components/foundations/OverflowingText';
import { AutoCompleteOptionType } from '../../../components/molecules/autocompleteTypes';
import { LayoutConfig } from '../../consts';

export const SKELETON_LOADING_TEST_ID = 'loadingSkeleton';

export const calculateElementOnHover = textList => {
    if (!textList?.length) return null;

    const isOnlyMainText = textList.length === 1;
    const formattedText = isOnlyMainText ? textList[0] : [...textList].sort((a, b) => a.length - b.length)[0];
    return (
        <>
            <OverflowingText hidePreview className={classNames(css.mainText, { [css.onlyMainText]: isOnlyMainText })}>
                {formattedText}
            </OverflowingText>
            {!isOnlyMainText && <div className={css.moreText}>{`(+${textList.length - 1} more)`}</div>}
        </>
    );
};

const MAX_DISPLAYED_MULTI_TAGS_VALUES = 3;

export const calculateMultiValuedTagTitle = textList => {
    const hiddenValuesAmount = textList.length - MAX_DISPLAYED_MULTI_TAGS_VALUES;
    const displayedTextList =
        hiddenValuesAmount > 0
            ? textList.slice(0, MAX_DISPLAYED_MULTI_TAGS_VALUES).concat(`+${hiddenValuesAmount} more`)
            : textList;
    return (
        <>
            {displayedTextList.map(text => (
                <div key={text}>{text}</div>
            ))}
        </>
    );
};

const CardTag = React.memo(({ category, value, title, maxWidth, ...translationProps }) => (
    <Tooltip title={title} disableInteractive>
        <span>
            <Tag
                className={css.tag}
                hidePreview={!!title}
                value={value}
                color={category && DimensionCategoryToColor[category]}
                {...translationProps}
            />
        </span>
    </Tooltip>
));

CardTag.propTypes = {
    category: PropTypes.string,
    value: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    translationData: PropTypes.shape({}),
    maxWidth: PropTypes.string,
};

const formatTranslationProps = (text, dataValue) => ({ value: text, translationData: { value: dataValue } });

export const DimensionToTagProps = {
    [GeneralDimensionsConfig[CAMPAIGN_NAME_FILTER].creativeField]: campaigns =>
        formatTranslationProps('STATIC.PAGES.CREATIVE_GALLERY.DIMENSIONS_INFO.CAMPAIGNS', campaigns.length),
    [GeneralDimensionsConfig[COUNTRY_FILTER].creativeField]: countries =>
        countries.length > 1
            ? formatTranslationProps('STATIC.PAGES.CREATIVE_GALLERY.DIMENSIONS_INFO.COUNTRIES', countries.length)
            : formatTranslationProps('STATIC.PAGES.CREATIVE_GALLERY.DIMENSIONS_INFO.COUNTRY', countries[0]),
};

const CreativeCard = React.forwardRef(
    (
        {
            url,
            type,
            width,
            height,
            imageHash,
            multiValuedDimensions = {},
            metrics,
            cohorts,
            selectedMetrics,
            selectedCohorts,
            aiTags = [],
            loadingSections = [],
            containerClass = '',
            onClick = () => {},
            showType = true,
            embeddingUrl,
            layout,
            cardHeightConfig,
            isAdminMode,
        },
        ref
    ) => {
        const assetRef = useRef();

        const [isLoading, setIsLoading] = useState(true);
        const [isHover, setIsHover] = useState(false);
        const [assetWidth, setAssetWidth] = useState(width);
        const [assetHeight, setAssetHeight] = useState(height);

        const isPlayable = type === CreativeTypes.PLAYABLE;

        const onError = async () => {
            setIsLoading(false);
        };

        const onLoad = () => {
            setIsLoading(false);
            if ((!assetWidth || !assetHeight) && assetRef.current) {
                setAssetWidth(assetRef.current.clientWidth);
                setAssetHeight(assetRef.current.clientHeight);
            }
        };

        const onCopyClick = (e, text) => {
            e.stopPropagation();
            navigator.clipboard.writeText(text);
        };

        const aspectRatio = useMemo(() => getCreativeSizeRatio(assetWidth, assetHeight, type), [
            assetWidth,
            assetHeight,
            type,
        ]);
        const creativeStyle = { aspectRatio };

        const isPlaceHolder = !url;
        const showAsset = !isPlaceHolder && !isLoading;

        const { assetNameList, creativeNameList } = multiValuedDimensions;

        const elementOnHover = calculateElementOnHover(assetNameList) ?? calculateElementOnHover(creativeNameList);

        const multiValuedTags = Object.entries(GeneralDimensionsConfig)
            .filter(([filterName, { isMultiValued, creativeField }]) => {
                if (!(isMultiValued && multiValuedDimensions[creativeField]?.length)) return false;
                return !!ASYNC_FILTER_TO_FIELD_CONFIG[filterName]?.showMultiValuedTag;
            })
            .map(entry => entry[1]);

        const showTagsSection = multiValuedTags.length || aiTags.some(value => value.tags.length);
        let cardStyle = {};
        if (layout === LayoutConfig.GRID.type && cardHeightConfig && !isLoading && !loadingSections?.length) {
            cardStyle = {
                '--card-height': `${cardHeightConfig.totalHeight}px`,
                '--tags-height': `${cardHeightConfig.sectionHeights.tags}px`,
                '--metrics-height': `${cardHeightConfig.sectionHeights.metrics}px`,
                '--events-height': `${cardHeightConfig.sectionHeights.events}px`,
                '--bottom-row-height': `${cardHeightConfig.sectionHeights.bottomRow}px`,
            };
        }

        return (
            <div
                ref={ref}
                onMouseEnter={() => isPlayable && setIsHover(true)}
                onMouseLeave={() => setIsHover(false)}
                className={classNames(css.container, containerClass, css[layout])}
                style={cardStyle}
                data-testid={url}
            >
                {!showAsset && (
                    <>
                        <Skeleton
                            animation="wave"
                            variant="rectangular"
                            className={css.assetSkeleton}
                            data-testid={SKELETON_LOADING_TEST_ID}
                            style={creativeStyle} // For testing purposes, we need to use the 'style' property.
                            sx={creativeStyle} // MUI overrides the 'style' property, requiring us to use 'sx'.
                        />
                        <Skeleton variant="text" className={css.metricsSkeleton} />
                        <Skeleton variant="circular" className={css.osSkeleton} />
                    </>
                )}
                <Fade in={showAsset} style={{ transitionDuration: '1s' }}>
                    <div
                        onClick={() => onClick(imageHash)}
                        className={classNames(css.creativeCard, { [css.show]: showAsset })}
                    >
                        <div className={classNames(css.creativeAssetContainer, css[layout])}>
                            {elementOnHover && (
                                <div className={css.assetHover}>
                                    <div className={css.hoverTextContainer}>{elementOnHover}</div>
                                </div>
                            )}
                            <CreativeAsset
                                showPlayable={isHover}
                                aspectRatio={aspectRatio}
                                onError={onError}
                                type={type}
                                ref={assetRef}
                                url={url}
                                embeddingUrl={embeddingUrl}
                                onLoad={onLoad}
                                className={classNames({
                                    [css.playableCard]: isPlayable && layout === LayoutConfig.GALLERY.type,
                                })}
                                showCreativeTypeIndication={showType}
                            />
                        </div>
                        {showTagsSection && (
                            <div className={classNames(css.tags, css.scrollableSection, css[layout])}>
                                {multiValuedTags.map(({ creativeField }) => (
                                    <CardTag
                                        key={creativeField}
                                        title={calculateMultiValuedTagTitle(multiValuedDimensions[creativeField])}
                                        shouldTranslate
                                        {...DimensionToTagProps[creativeField](multiValuedDimensions[creativeField])}
                                    />
                                ))}
                                {aiTags.map(({ tags, category, dimensionName }) =>
                                    tags.map(tag => (
                                        <CardTag
                                            key={`${dimensionName}-${tag}`}
                                            title={
                                                <Translate
                                                    id="STATIC.PAGES.CREATIVE_GALLERY.DIMENSIONS_INFO.DIMENSION"
                                                    data={{ value: dimensionName }}
                                                />
                                            }
                                            value={tag}
                                            category={category}
                                        />
                                    ))
                                )}
                            </div>
                        )}
                        <CreativeCardData
                            osList={multiValuedDimensions?.osList || []}
                            metrics={metrics}
                            cohorts={cohorts}
                            selectedMetrics={selectedMetrics}
                            selectedCohorts={selectedCohorts}
                            loadingSections={loadingSections}
                            url={url}
                            layout={layout}
                            cardStyle={cardStyle}
                        />
                        {isAdminMode && (
                            <div className={css.adminAdditionalCreativeInfo}>
                                <OverflowingText
                                    onClick={e => onCopyClick(e, imageHash)}
                                    className={css.adminDataSection}
                                >
                                    Hash: {imageHash}
                                </OverflowingText>
                                <OverflowingText onClick={e => onCopyClick(e, url)} className={css.adminDataSection}>
                                    URL: {url}
                                </OverflowingText>
                            </div>
                        )}
                    </div>
                </Fade>
            </div>
        );
    }
);

const DimensionTagsProp = PropTypes.shape({
    dimensionName: PropTypes.string,
    category: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
});

CreativeCard.propTypes = {
    ...CreativeType,
    multiValuedDimensions: PropTypes.shape(CreativeMultiValuedDimensionsType),
    showType: PropTypes.bool,
    onClick: PropTypes.func,
    containerClass: PropTypes.string,
    selectedMetrics: PropTypes.arrayOf(AutoCompleteOptionType),
    selectedCohorts: PropTypes.arrayOf(AutoCompleteOptionType),
    aiTags: PropTypes.arrayOf(DimensionTagsProp),
    loadingSections: PropTypes.arrayOf(PropTypes.string),
};

export default CreativeCard;
